import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Plus, ArrowRight, Eye, Rss, Link2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { useLang } from "../../context/LanguageContext";
import StatusBadge from "../../components/StatusBadge";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const imgUrl = (id) => `${BACKEND}/api/publications/${id}/image`;

export default function Dashboard() {
  const { user, setUser } = useAuth();
  const { t } = useLang();
  const [stats, setStats] = useState(null);
  const [projects, setProjects] = useState([]);
  const [posts, setPosts] = useState([]);
  const [becoming, setBecoming] = useState(false);

  useEffect(() => {
    api.get("/dashboard/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/projects/mine").then((r) => setProjects(r.data)).catch(() => {});
    api.get("/publications").then((r) => setPosts(r.data)).catch(() => {});
  }, []);

  // Devenir partenaire en 1 clic (le compte utilisateur est conservé).
  const becomePartner = async () => {
    setBecoming(true);
    try {
      await api.post("/partner/become");
      const me = await api.get("/auth/me");
      setUser(me.data);
      toast.success("Vous êtes maintenant partenaire ! Découvrez votre espace partenaire.");
    } catch (e) { toast.error(e?.response?.data?.detail || "Une erreur est survenue."); }
    finally { setBecoming(false); }
  };

  const cards = stats ? [
    { v: stats.total, l: t("u_s_ideas") },
    { v: stats.in_study, l: t("u_s_study") },
    { v: stats.preselected + stats.finalists, l: t("u_s_pre") },
    { v: stats.selected, l: t("u_s_selected") },
  ] : [];

  return (
    <div className="mx-auto max-w-6xl">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-[11px] uppercase tracking-[0.25em] text-cyan">{t("u_dash_kicker")}</div>
          <h1 className="mt-3 font-display text-3xl font-900 text-white sm:text-4xl">{t("u_greeting")} {user?.first_name}</h1>
        </div>
        <Link to="/app/deposer" data-testid="dash-new-idea" className="group inline-flex items-center gap-2 rounded-full bg-cyan px-6 py-3.5 text-sm font-600 text-void transition-all hover:-translate-y-0.5 hover:glow-cyan">
          <Plus size={17} /> {t("u_new_idea")}
        </Link>
      </div>

      <div className="mt-10 grid gap-px border border-white/10 bg-white/10 sm:grid-cols-2 lg:grid-cols-4">
        {cards.map((c, i) => (
          <motion.div key={i} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.06 }} className="bg-void p-6" data-testid={`dash-stat-${i}`}>
            <div className="font-display text-4xl font-900 tabular-nums text-white">{c.v}</div>
            <div className="mt-2 text-sm text-ink-secondary">{c.l}</div>
          </motion.div>
        ))}
      </div>

      {/* Devenir partenaire : proposé à chaque utilisateur */}
      {user && user.role === "user" && (
        <div className="mt-8 flex flex-wrap items-center justify-between gap-4 rounded-2xl border border-cyan/25 bg-cyan/[0.06] p-5" data-testid="dash-become-partner">
          <div className="flex items-start gap-3">
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-cyan/15 text-cyan"><Link2 size={18} /></span>
            <div>
              <div className="font-display text-base font-700 text-white">Devenez partenaire</div>
              <p className="mt-1 max-w-xl text-xs leading-relaxed text-ink-secondary">
                Recommandez APP CHALLENGE ACADEMY autour de vous : recevez un lien unique et suivez en direct
                les inscriptions que vous générez dans un espace dédié.
              </p>
            </div>
          </div>
          {user.partner_code ? (
            <Link to="/partenaire/espace" data-testid="dash-goto-partner"
              className="inline-flex items-center gap-2 rounded-full bg-cyan px-5 py-2.5 text-sm font-600 text-void transition-all hover:glow-cyan">
              <CheckCircle2 size={16} /> Mon espace partenaire
            </Link>
          ) : (
            <button onClick={becomePartner} disabled={becoming} data-testid="dash-become-btn"
              className="inline-flex items-center gap-2 rounded-full bg-cyan px-5 py-2.5 text-sm font-600 text-void transition-all hover:glow-cyan disabled:opacity-60">
              <Link2 size={16} /> {becoming ? "Activation…" : "Devenir partenaire"}
            </button>
          )}
        </div>
      )}

      <div className="mt-12">
        <h2 className="mb-5 font-display text-xl font-700 text-white">{t("u_recent")}</h2>
        {projects.length === 0 ? (
          <div className="border border-dashed border-white/15 p-12 text-center">
            <p className="text-ink-secondary">{t("u_empty")}</p>
            <Link to="/app/deposer" className="mt-4 inline-flex items-center gap-2 text-sm text-cyan hover:underline">
              {t("u_first")} <ArrowRight size={15} />
            </Link>
          </div>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {projects.slice(0, 6).map((p) => (
              <Link key={p.id} to={p.status === "BROUILLON" ? `/app/deposer/${p.id}` : `/app/projets/${p.id}`} data-testid={`dash-project-${p.id}`}
                className="group border border-white/10 bg-surface p-5 transition-all hover:-translate-y-0.5 hover:border-cyan/40">
                <div className="flex items-center justify-between">
                  {p.logo ? <img src={p.logo} alt="" className="h-10 w-10 object-contain" /> : <div className="flex h-10 w-10 items-center justify-center bg-elevated font-display text-cyan">{(p.app_name || p.title || "?")[0]}</div>}
                  <StatusBadge status={p.status} />
                </div>
                <div className="mt-4 font-display text-lg font-600 text-white line-clamp-1">{p.app_name || p.title || t("u_untitled")}</div>
                <div className="mt-1 text-xs text-ink-tertiary">{p.ref}</div>
              </Link>
            ))}
          </div>
        )}
      </div>

      {posts.length > 0 && (
        <div className="mt-12" data-testid="dash-blog-block">
          <div className="mb-5 flex items-center justify-between">
            <h2 className="inline-flex items-center gap-2 font-display text-xl font-700 text-white"><Rss size={18} className="text-cyan" /> Actualités du blog</h2>
            <Link to="/blog" data-testid="dash-blog-all" className="inline-flex items-center gap-1.5 text-sm text-cyan hover:underline">Voir le blog <ArrowRight size={14} /></Link>
          </div>
          <div className="grid gap-4 sm:grid-cols-3">
            {posts.slice(0, 3).map((p) => (
              <Link key={p.id} to={`/blog/${p.id}`} data-testid={`dash-blog-${p.id}`}
                className="group overflow-hidden border border-white/10 bg-surface transition-all hover:-translate-y-0.5 hover:border-cyan/40">
                <div className="aspect-[16/10] overflow-hidden bg-elevated">
                  {p.has_image ? <img src={imgUrl(p.id)} alt="" loading="lazy" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" /> : <div className="flex h-full items-center justify-center text-cyan/30"><Rss size={28} /></div>}
                </div>
                <div className="p-4">
                  <div className="font-display text-base font-600 text-white line-clamp-2 group-hover:text-cyan">{p.title}</div>
                  <div className="mt-2 inline-flex items-center gap-1.5 text-xs text-ink-tertiary"><Eye size={12} className="text-cyan" /> {p.views || 0} vue{(p.views || 0) > 1 ? "s" : ""}</div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
